import axiosInstance from './axiosInstance'

const CANTON_BASE = '/cantons'

let cantonesCache = null
let cacheTimestamp = null
const CACHE_TTL = 5 * 60 * 1000 // 5 minutos

/**
 * Obtiene todos los cantones habilitados con sus tenants
 * @returns {Promise<Array>} Lista de cantones con tenants anidados
 */
export const fetchCantonesWithTenants = async (options = {}) => {
  const { force = false, noCache = false, throwOnError = false } = options
  const bypassCache = force || noCache
  const now = Date.now()

  if (!bypassCache && cantonesCache && cacheTimestamp && now - cacheTimestamp < CACHE_TTL) {
    return cantonesCache
  }

  try {
    const requestConfig = {}

    if (noCache) {
      requestConfig.headers = {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: '0',
      }
      requestConfig.params = { _ts: now }
    }

    const response = await axiosInstance.get(`${CANTON_BASE}/with-tenants`, requestConfig)
    cantonesCache = response.data.cantones || []
    cacheTimestamp = now
    return cantonesCache
  } catch (error) {
    console.error('Error fetching cantones:', error)
    if (throwOnError) throw error
    return cantonesCache || []
  }
}

/**
 * Obtiene un cantón por slug con sus tenants
 * @param {string} slug - Slug del cantón
 * @returns {Promise<Object|null>} Cantón con tenants o null
 */
export const fetchCantonBySlug = async slug => {
  // Primero intentar desde cache
  if (cantonesCache) {
    const cached = cantonesCache.find(c => c.slug === slug)
    if (cached) return cached
  }

  try {
    const response = await axiosInstance.get(`${CANTON_BASE}/by-slug/${slug}`)
    return response.data.canton || null
  } catch (error) {
    console.error(`Error fetching canton ${slug}:`, error)
    return null
  }
}

/**
 * Obtiene los tenants de un cantón
 * @param {string} cantonSlug - Slug del cantón
 * @returns {Promise<Array>} Lista de tenants
 */
export const fetchTenantsByCanton = async cantonSlug => {
  try {
    const response = await axiosInstance.get(`${CANTON_BASE}/by-slug/${cantonSlug}/tenants`)
    return response.data.tenants || []
  } catch (error) {
    console.error(`Error fetching tenants for ${cantonSlug}:`, error)
    return []
  }
}

/**
 * Obtiene el boundary GeoJSON de un cantón
 * @param {string} slug - Slug del cantón
 * @returns {Promise<Object|null>} GeoJSON o null
 */
export const fetchCantonBoundary = async slug => {
  try {
    const response = await axiosInstance.get(`${CANTON_BASE}/by-slug/${slug}/boundary`)
    return response.data
  } catch (error) {
    console.error(`Error fetching boundary for ${slug}:`, error)
    return null
  }
}

/**
 * Obtiene slugs válidos de cantones
 * @returns {Promise<Array>} Lista de slugs
 */
export const fetchValidCantonSlugs = async () => {
  try {
    const response = await axiosInstance.get(`${CANTON_BASE}/slugs`)
    return response.data.slugs || []
  } catch (error) {
    console.error('Error fetching canton slugs:', error)
    return []
  }
}

/**
 * Invalida el cache de cantones
 */
export const invalidateCantonesCache = () => {
  cantonesCache = null
  cacheTimestamp = null
}

/**
 * Actualiza la lista de slugs válidos en el detector de ciudades
 * @param {Array} slugs - Lista de slugs válidos
 */
export const updateValidCantonSlugs = slugs => {
  if (typeof window !== 'undefined') {
    window.__validCantonSlugs = slugs
  }
}

/**
 * Obtiene la lista de slugs válidos desde memoria
 * @returns {Array} Lista de slugs
 */
export const getValidCantonSlugsFromMemory = () => {
  if (typeof window !== 'undefined' && window.__validCantonSlugs) {
    return window.__validCantonSlugs
  }
  return []
}

/**
 * Obtiene todos los cantones (para administración)
 * @returns {Promise<Array>} Lista de cantones
 */
export const fetchAllCantones = async () => {
  try {
    const response = await axiosInstance.get(CANTON_BASE)
    // El backend devuelve { res: data } con datos crudos, mapeamos a formato frontend
    const rawData = response.data.cantones || response.data.res || []
    return rawData.map(c => ({
      id: c.id || c.id_ca,
      slug: c.slug,
      name: c.name || c.nombre,
      center: c.center || (c.center_lng && c.center_lat ? [parseFloat(c.center_lng), parseFloat(c.center_lat)] : null),
      zoom: c.zoom || 12,
      enabled: c.enabled !== undefined ? Boolean(c.enabled) : Boolean(c.habilitado),
      hasGeojson: Boolean(c.hasGeojson),
      idProvincia: c.idProvincia,
    }))
  } catch (error) {
    console.error('Error fetching all cantones:', error)
    return []
  }
}

/**
 * Crea un nuevo cantón
 * @param {Object} data - Datos del cantón
 */
export const createCanton = async data => {
  try {
    const response = await axiosInstance.post(CANTON_BASE, data)
    invalidateCantonesCache()
    return { success: true, canton: response.data.canton }
  } catch (error) {
    console.error('Error creating canton:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Error al crear el cantón',
    }
  }
}

/**
 * Actualiza un cantón existente
 * @param {number} id - ID del cantón
 * @param {Object} data - Datos a actualizar
 */
export const updateCanton = async (id, data) => {
  try {
    const response = await axiosInstance.put(`${CANTON_BASE}/${id}`, data)
    invalidateCantonesCache()
    return { success: true, canton: response.data.canton }
  } catch (error) {
    console.error('Error updating canton:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Error al actualizar el cantón',
    }
  }
}

/**
 * Elimina un cantón
 * @param {number} id - ID del cantón
 */
export const deleteCanton = async id => {
  try {
    await axiosInstance.delete(`${CANTON_BASE}/${id}`)
    invalidateCantonesCache()
    return { success: true }
  } catch (error) {
    console.error('Error deleting canton:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Error al eliminar el cantón',
    }
  }
}

/**
 * Habilita un cantón
 * @param {number} id - ID del cantón
 */
export const enableCanton = async id => {
  try {
    const response = await axiosInstance.patch(`${CANTON_BASE}/${id}/enable`)
    invalidateCantonesCache()
    return { success: true, canton: response.data.canton }
  } catch (error) {
    console.error('Error enabling canton:', error)
    return { success: false, message: 'Error al habilitar el cantón' }
  }
}

/**
 * Deshabilita un cantón
 * @param {number} id - ID del cantón
 */
export const disableCanton = async id => {
  try {
    const response = await axiosInstance.patch(`${CANTON_BASE}/${id}/disable`)
    invalidateCantonesCache()
    return { success: true, canton: response.data.canton }
  } catch (error) {
    console.error('Error disabling canton:', error)
    return { success: false, message: 'Error al deshabilitar el cantón' }
  }
}

/**
 * Obtiene provincias (para el selector de cantones)
 * @returns {Promise<Array>} Lista de provincias
 */
export const fetchProvincias = async () => {
  try {
    const response = await axiosInstance.get('/provinces')
    // El backend devuelve { res: provinces } con id_pv, mapeamos a formato esperado
    const rawData = response.data.provincias || response.data.res || []
    return rawData.map(p => ({
      id: p.id || p.id_pv,
      nombre: p.nombre,
      habilitado: p.habilitado,
    }))
  } catch (error) {
    console.error('Error fetching provincias:', error)
    return []
  }
}
